import { z } from "zod"
import { EmergenceDistribution, SpecialOpsCode } from "./special-operation-codes.js"
import {
	BannerHPDataSchema,
	CanalHomeDataSchema,
	ClassiqueHomeDataSchema,
	Club300DataSchema,
	DisneyHomeDataSchema,
	DolbyHomeDataSchema,
	EmergenceMovieDataSchema,
	EmergenceSeriesDataSchema,
	EmergenceSVODDataSchema,
	EmergenceTheaterDataSchema,
	FestivalSectionDataSchema,
	FilmesOnlineDataSchema,
	IMAXHomeDataSchema,
	IndelebileHomeDataSchema,
	M6HomeDataSchema,
	MaxHomeDataSchema,
	MobileDdayDataSchema,
	NovelasHomeDataSchema,
	RollerHomeDataSchema,
	SponsoredAutocompleteDataSchema,
	TF1HomeDataSchema,
	TicketAnchorDataSchema,
	VODHomeDataSchema,
} from "./special-operation-schemas.js"

const filledString = z.string().trim().min(1)

// Anything but a plain object (absent, null, "", [], a number) counts as empty, so each missing leaf reports its own
// path (medias.desktop).
const requiredObject = <TShape extends z.ZodRawShape>(shape: TShape) =>
	z.preprocess(
		(value) => (value !== null && typeof value === "object" && !Array.isArray(value) ? value : {}),
		z.object(shape),
	)

const releaseDateRequired = { release_date: z.iso.datetime() }

/**
 * Only the fields an operation must fill to be active, per code. Checking these alone keeps a payload as GraphQL
 * serializes it (nulls, `__typename`) valid for activation, where the strict data schema would reject it.
 * Activation checks presence and shape; writes reach it only after strict data validation, so a present field is
 * already well-formed there.
 * Key order is the order the missing paths are reported in (the explicit-activation error message lists them).
 */
const activationFieldsByCode = {
	[SpecialOpsCode.BANNER_HP]: z.object({
		link_desktop: z.url(),
		medias: requiredObject({ desktop: z.url(), mobile: z.url() }),
	}),
	[SpecialOpsCode.CANAL_HOME]: z.object({}),
	[SpecialOpsCode.CLASSIQUE_HOME]: z.object({}),
	[SpecialOpsCode.CLUB300]: z.object({}),
	[SpecialOpsCode.DISNEY_HOME]: z.object({}),
	[SpecialOpsCode.DOLBY_HOME]: z.object({}),
	[SpecialOpsCode.EMERGENCE]: z.object(releaseDateRequired),
	[SpecialOpsCode.EMERGENCE_MOVIE]: z.object({
		...releaseDateRequired,
		distribution: z.enum(EmergenceDistribution),
	}),
	[SpecialOpsCode.EMERGENCE_SERIES]: z.object(releaseDateRequired),
	[SpecialOpsCode.EMERGENCE_SVOD]: z.object(releaseDateRequired),
	[SpecialOpsCode.FESTIVAL_SECTION]: z.object({
		festivalEditionId: z.number().int(),
		festivalId: z.number().int(),
	}),
	[SpecialOpsCode.FILMES_ONLINE]: z.object({}),
	[SpecialOpsCode.IMAX_HOME]: z.object({}),
	[SpecialOpsCode.INDELEBILE_HOME]: z.object({}),
	[SpecialOpsCode.M6_HOME]: z.object({}),
	[SpecialOpsCode.MAX_HOME]: z.object({}),
	[SpecialOpsCode.MOBILE_DDAY]: z.object({
		dday_campaign: filledString,
		dday_movie_id: z.number().int(),
		dday_date: filledString,
		dday_last_date: filledString,
	}),
	[SpecialOpsCode.NOVELAS_HOME]: z.object({}),
	[SpecialOpsCode.ROLLER_HOME]: z.object({}),
	[SpecialOpsCode.SPONSORED_AUTOCOMPLETE]: z.object({}),
	[SpecialOpsCode.TF1_HOME]: z.object({}),
	[SpecialOpsCode.TICKET_ANCHOR]: z.object({
		link: z.url(),
		...releaseDateRequired,
		medias: requiredObject({ desktopHp: z.url(), desktop: z.url(), mobile: z.url() }),
	}),
	[SpecialOpsCode.VOD_HOME]: z.object({}),
} satisfies Record<SpecialOpsCode, z.ZodObject>

export const BannerHPActivationSchema = BannerHPDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.BANNER_HP].shape,
)
export const CanalHomeActivationSchema = CanalHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.CANAL_HOME].shape,
)
export const ClassiqueHomeActivationSchema = ClassiqueHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.CLASSIQUE_HOME].shape,
)
export const Club300ActivationSchema = Club300DataSchema.extend(activationFieldsByCode[SpecialOpsCode.CLUB300].shape)
export const DisneyHomeActivationSchema = DisneyHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.DISNEY_HOME].shape,
)
export const DolbyHomeActivationSchema = DolbyHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.DOLBY_HOME].shape,
)
export const EmergenceMovieActivationSchema = EmergenceMovieDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.EMERGENCE_MOVIE].shape,
)
export const EmergenceSeriesActivationSchema = EmergenceSeriesDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.EMERGENCE_SERIES].shape,
)
export const EmergenceSVODActivationSchema = EmergenceSVODDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.EMERGENCE_SVOD].shape,
)
export const EmergenceTheaterActivationSchema = EmergenceTheaterDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.EMERGENCE].shape,
)
export const FestivalSectionActivationSchema = FestivalSectionDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.FESTIVAL_SECTION].shape,
)
export const FilmesOnlineActivationSchema = FilmesOnlineDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.FILMES_ONLINE].shape,
)
export const IMAXHomeActivationSchema = IMAXHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.IMAX_HOME].shape,
)
export const IndelebileHomeActivationSchema = IndelebileHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.INDELEBILE_HOME].shape,
)
export const M6HomeActivationSchema = M6HomeDataSchema.extend(activationFieldsByCode[SpecialOpsCode.M6_HOME].shape)
export const MaxHomeActivationSchema = MaxHomeDataSchema.extend(activationFieldsByCode[SpecialOpsCode.MAX_HOME].shape)
export const MobileDdayActivationSchema = MobileDdayDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.MOBILE_DDAY].shape,
)
export const NovelasHomeActivationSchema = NovelasHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.NOVELAS_HOME].shape,
)
export const RollerHomeActivationSchema = RollerHomeDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.ROLLER_HOME].shape,
)
export const SponsoredAutocompleteActivationSchema = SponsoredAutocompleteDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.SPONSORED_AUTOCOMPLETE].shape,
)
export const TF1HomeActivationSchema = TF1HomeDataSchema.extend(activationFieldsByCode[SpecialOpsCode.TF1_HOME].shape)
export const TicketAnchorActivationSchema = TicketAnchorDataSchema.extend(
	activationFieldsByCode[SpecialOpsCode.TICKET_ANCHOR].shape,
)
export const VODHomeActivationSchema = VODHomeDataSchema.extend(activationFieldsByCode[SpecialOpsCode.VOD_HOME].shape)

interface GetMissingActivationFieldsParams {
	code: SpecialOpsCode
	data: unknown
}

/**
 * Dot paths of the activation fields that are missing or invalid; null or non-object data counts as empty.
 * @example getMissingActivationFields({ code: SpecialOpsCode.BANNER_HP, data: { medias: null } }) // ["link_desktop", "medias.desktop", "medias.mobile"]
 */
export const getMissingActivationFields = ({ code, data }: GetMissingActivationFieldsParams) => {
	const result = activationFieldsByCode[code].safeParse(data !== null && typeof data === "object" ? data : {})
	return result.success ? [] : [...new Set(result.error.issues.map((issue) => issue.path.join(".")))]
}
